// Funkcje pomocnicze formatowania (PL).
import { PRODUCT_META } from "./types";

const dateTimeFmt = new Intl.DateTimeFormat("pl-PL", { dateStyle: "medium", timeStyle: "short" });
const dateFmt = new Intl.DateTimeFormat("pl-PL", { dateStyle: "medium" });
const dayMonthFmt = new Intl.DateTimeFormat("pl-PL", { day: "2-digit", month: "2-digit" });

function parse(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDateTime(value: string) {
  const date = parse(value);
  return date ? dateTimeFmt.format(date) : value || "—";
}

export function formatDate(value: string) {
  const date = parse(value);
  return date ? dateFmt.format(date) : value || "—";
}

export function formatDayMonth(date: Date) {
  return dayMonthFmt.format(date);
}

// Względny czas po polsku: „przed chwilą”, „14 min temu”, „3 godz. temu”, „wczoraj”, „5 dni temu”.
export function relativeTime(value: string) {
  const date = parse(value);
  if (!date) return value || "—";
  const diffMs = Date.now() - date.getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 1) return "przed chwilą";
  if (min < 60) return `${min} min temu`;
  const hours = Math.round(min / 60);
  if (hours < 24) return `${hours} godz. temu`;
  const days = Math.round(hours / 24);
  if (days === 1) return "wczoraj";
  if (days < 30) return `${days} dni temu`;
  return formatDate(value);
}

export function productLabel(value: string, short = false) {
  const meta = PRODUCT_META[value];
  if (!meta) return value || "—";
  return short ? meta.short : meta.label;
}

export function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "??";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const currencyFmt = new Intl.NumberFormat("pl-PL", {
  style: "currency",
  currency: "PLN",
  maximumFractionDigits: 0
});

export function formatCurrency(value: number | null | undefined) {
  if (value == null || Number.isNaN(value)) return "—";
  return currencyFmt.format(value);
}

export function formatNumber(value: number) {
  return new Intl.NumberFormat("pl-PL").format(value);
}

// Stabilny, lekki indeks koloru awatara na podstawie tekstu.
export function hashHue(text: string) {
  let h = 0;
  for (let i = 0; i < text.length; i++) h = (h * 31 + text.charCodeAt(i)) % 360;
  return h;
}

// ---- Zadania / terminy ----

export type TaskBucket = "overdue" | "today" | "tomorrow" | "week" | "later" | "done";

function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

export function taskBucket(dueAt: string, done: boolean): TaskBucket {
  if (done) return "done";
  const due = parse(dueAt);
  if (!due) return "later";
  const now = Date.now();
  if (due.getTime() < now) return "overdue";
  const start = startOfToday().getTime();
  const day = 86_400_000;
  if (due.getTime() < start + day) return "today";
  if (due.getTime() < start + 2 * day) return "tomorrow";
  if (due.getTime() < start + 7 * day) return "week";
  return "later";
}

const timeFmt = new Intl.DateTimeFormat("pl-PL", { hour: "2-digit", minute: "2-digit" });
const weekdayFmt = new Intl.DateTimeFormat("pl-PL", { weekday: "short" });
const shortDateFmt = new Intl.DateTimeFormat("pl-PL", { day: "numeric", month: "short" });

function capitalize(value: string) {
  return value ? value[0].toUpperCase() + value.slice(1) : value;
}

// Przyjazny opis terminu: „Dziś, 17:00", „Jutro, 09:00", „Pon, 14:00", „2 cze, 10:00", „Zaległe · 28 maj, 14:00".
export function formatDue(dueAt: string, done = false): string {
  const due = parse(dueAt);
  if (!due) return dueAt || "—";
  const time = timeFmt.format(due);
  if (done) return `${shortDateFmt.format(due)}, ${time}`;
  const bucket = taskBucket(dueAt, false);
  if (bucket === "overdue") return `Zaległe · ${shortDateFmt.format(due)}, ${time}`;
  if (bucket === "today") return `Dziś, ${time}`;
  if (bucket === "tomorrow") return `Jutro, ${time}`;
  if (bucket === "week") return `${capitalize(weekdayFmt.format(due))}, ${time}`;
  return `${shortDateFmt.format(due)}, ${time}`;
}

// Wartość dla <input type="datetime-local"> (czas lokalny).
export function toDateTimeLocal(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}
